import { Button, Div, Flex, FormStatus, InfoCard, P, ProgressIndicator } from '@dnb/eufemia';
import { type JSX, useMemo } from 'react';

import { useDebounce } from '@/hooks/useDebounce';
import { ApiClientResponse } from '@/openapi/schema';

import CiamClientTile from '../CiamClientTile';
import ClientTile from '../ClientTile';
import { ViewType } from '../schemas';
import useCiamClients from '../useCiamClients';

function CiamClientsSection({
  apiSlug,
  portalClientIds,
  debouncedQuery,
  portalClientElements,
}: {
  apiSlug: string;
  portalClientIds: Set<string>;
  debouncedQuery: string;
  portalClientElements: JSX.Element[];
}) {
  const {
    ciamClients,
    isLoading: ciamLoading,
    error: ciamError,
  } = useCiamClients({
    apiSlug,
    portalClientIds,
    debouncedQuery,
  });

  const ciamClientElements = ciamClients.map((client) => (
    <Div bottom="small" key={client.id}>
      <CiamClientTile client={client} />
    </Div>
  ));

  const allClients = [...portalClientElements, ...ciamClientElements];

  return (
    <>
      {ciamLoading && <ProgressIndicator size="small" type="linear" />}
      {ciamError && <FormStatus bottom="small" state="error" stretch text="Failed to load CIAM clients." />}
      {allClients.length > 0 ? allClients : <InfoCard stretch text="No clients match the current filters." />}
    </>
  );
}

export default function ClientView({
  consumerClients,
  isLoading,
  showView,
  query,
  apiSlug,
  showAllClients,
  onShowCiamClients,
}: {
  consumerClients: ApiClientResponse[] | undefined;
  isLoading: boolean;
  showView: (view: ViewType, query?: string) => void;
  query: string;
  apiSlug: string;
  showAllClients: boolean;
  onShowCiamClients: () => void;
}) {
  const debouncedQuery = useDebounce(query, 300);

  const portalClientIds = useMemo(() => new Set(consumerClients?.map((c) => c.ciamClientId) ?? []), [consumerClients]);

  const filteredConsumerClients = useMemo(() => {
    if (!consumerClients) return [];

    const lowerCaseQuery = debouncedQuery.toLowerCase();
    if (!lowerCaseQuery) {
      return consumerClients.map((client) => (
        <Div bottom="small" key={client.ciamClientId}>
          <ClientTile client={client} showView={showView} />
        </Div>
      ));
    }

    return consumerClients
      .filter((client) => {
        const clientName = client.name?.toLowerCase() || '';
        const clientId = client.ciamClientId?.toLowerCase() || '';
        const clientDescription = client.description?.toLowerCase() || '';
        const appId = client.appId?.toLowerCase() || '';
        const liveMode = client.liveMode ? 'live' : 'test';
        const scopes = client.scopes ? client.scopes.map((scope) => scope.toLowerCase()) : [];
        const ciamApplicationId = client.ciamAppId?.toLowerCase() || '';

        return (
          clientName.includes(lowerCaseQuery) ||
          clientId.includes(lowerCaseQuery) ||
          clientDescription.includes(lowerCaseQuery) ||
          appId.includes(lowerCaseQuery) ||
          liveMode.includes(lowerCaseQuery) ||
          scopes.some((scope) => scope.includes(lowerCaseQuery)) ||
          ciamApplicationId.includes(lowerCaseQuery)
        );
      })
      .map((client) => (
        <Div bottom="small" key={client.ciamClientId}>
          <ClientTile client={client} showView={showView} />
        </Div>
      ));
  }, [consumerClients, debouncedQuery, showView]);

  return (
    <>
      {!showAllClients && consumerClients && consumerClients.length > 0 && (
        <FormStatus bottom="small" state="warning" stretch>
          <Flex.Container align="center" justify="space-between" stretch>
            <Flex.Item>Only clients onboarded in the developer portal are displayed.</Flex.Item>

            <Flex.Item>
              <Button on_click={onShowCiamClients} size="small" variant="tertiary">
                Show all CIAM clients
              </Button>
            </Flex.Item>
          </Flex.Container>
        </FormStatus>
      )}
      {isLoading && <ProgressIndicator size="small" type="linear" />}
      {showAllClients ? (
        <CiamClientsSection
          apiSlug={apiSlug}
          debouncedQuery={debouncedQuery}
          portalClientElements={filteredConsumerClients}
          portalClientIds={portalClientIds}
        />
      ) : !isLoading && consumerClients?.length === 0 ? (
        <InfoCard stretch>
          <P>
            No portal clients have been granted access to this integration service. There may still be CIAM clients with
            access.
          </P>
          <Button on_click={onShowCiamClients} size="small" top="x-small" variant="tertiary">
            Check CIAM clients
          </Button>
        </InfoCard>
      ) : filteredConsumerClients.length > 0 ? (
        filteredConsumerClients
      ) : (
        <InfoCard stretch text="No clients match the current filters." />
      )}
    </>
  );
}
